import React from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";

const META = {
  hazards: {
    title: "유해·위험요인",
    description: "현재 평가 주기에 연결된 위험요인입니다.",
    color: "#d97706",
    soft: "#fffbeb",
    icon: <WarningAmberRoundedIcon fontSize="small" />,
  },
  evaluated: {
    title: "평가 완료",
    description: "현재 평가 주기에 위험도 평가가 완료된 항목입니다.",
    color: "#059669",
    soft: "#ecfdf5",
    icon: <VerifiedOutlinedIcon fontSize="small" />,
  },
  improving: {
    title: "개선 진행",
    description: "개선대책을 수립하고 조치를 진행 중인 항목입니다.",
    color: "#ea580c",
    soft: "#fff7ed",
    icon: <BuildCircleOutlinedIcon fontSize="small" />,
  },
  highestRisk: {
    title: "최고 위험성",
    description: "현재 주기에서 위험도 점수가 가장 높은 항목입니다.",
    color: "#dc2626",
    soft: "#fef2f2",
    icon: <WarningAmberRoundedIcon fontSize="small" />,
  },
};

const STATUS_LABEL = {
  not_evaluated: "미평가",
  measure_required: "개선 필요",
  improving: "개선 중",
  improvement_completed: "개선 완료",
  evaluated: "평가 완료",
};

const dateLabel = (value) => (value ? String(value).slice(0, 10) : "-");

const Detail = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ color: "#667085", fontSize: 11.5 }}>{label}</Typography>
    <Typography sx={{ mt: 0.25, color: "#1d2939", fontSize: 13, lineHeight: 1.45, overflowWrap: "anywhere" }}>
      {value ?? "-"}
    </Typography>
  </Box>
);

export default function MachineSafetyMetricDialog({
  open,
  onClose,
  type,
  items = [],
  machineName,
  periodLabel,
  onOpenSafety,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const meta = META[type] || META.hazards;
  const safeItems = Array.isArray(items)
    ? items.filter((item) => item && typeof item === "object")
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 2.5, maxHeight: "88vh" } }}
    >
      <DialogTitle sx={{ p: 2, pr: 6 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Box sx={{ width: 38, height: 38, borderRadius: 1.5, bgcolor: meta.soft, color: meta.color, display: "grid", placeItems: "center" }}>
            {meta.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ color: "#101828", fontSize: 17, fontWeight: 700 }}>{meta.title}</Typography>
              <Chip size="small" label={`${safeItems.length}건`} sx={{ height: 22, bgcolor: meta.soft, color: meta.color, fontWeight: 700 }} />
            </Stack>
            <Typography noWrap sx={{ mt: 0.25, color: "#667085", fontSize: 12 }}>
              {machineName || "설비·구역"}{periodLabel ? ` · ${periodLabel}` : ""}
            </Typography>
          </Box>
        </Stack>
        <IconButton aria-label="닫기" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: "#f7f8fa" }}>
        <Typography sx={{ mb: 1.5, color: "#667085", fontSize: 12.5 }}>{meta.description}</Typography>
        <Stack spacing={1}>
          {safeItems.map((item, index) => (
            <Box
              key={`${item.register_cause_id || item.eval_id || "item"}-${index}`}
              sx={{ p: { xs: 1.4, sm: 1.7 }, bgcolor: "#fff", border: "1px solid #e4e7ec", borderRadius: 2 }}
            >
              <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: "#101828", fontSize: 14, fontWeight: 650, lineHeight: 1.5, overflowWrap: "anywhere" }}>
                    {item.danger_description || "위험요인 내용 미등록"}
                  </Typography>
                  <Typography sx={{ mt: 0.35, color: "#667085", fontSize: 11.5 }}>
                    {item.process_name || "평가 대상 미지정"} · {item.danger_category || "분류 미지정"}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={item.effective_risk_score == null ? "미평가" : `${item.effective_risk_score}점`}
                  sx={{ flexShrink: 0, height: 24, bgcolor: item.effective_risk_score == null ? "#f2f4f7" : meta.soft, color: item.effective_risk_score == null ? "#667085" : meta.color, fontWeight: 700 }}
                />
              </Stack>

              <Box sx={{ mt: 1.25, pt: 1.15, borderTop: "1px solid #eaecf0", display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1.25 }}>
                <Detail label="상태" value={STATUS_LABEL[item.status] || "확인 필요"} />
                <Detail label="평가일" value={dateLabel(item.eval_date)} />
                <Detail label="가능성" value={item.frequency_level == null ? "-" : `${item.frequency_level}점`} />
                <Detail label="중대성" value={item.importancy_level == null ? "-" : `${item.importancy_level}점`} />
              </Box>

              {type === "improving" && (
                <Box sx={{ mt: 1.25, p: 1.2, borderRadius: 1.5, bgcolor: "#f8fafc" }}>
                  <Typography sx={{ color: "#667085", fontSize: 11.5 }}>개선대책</Typography>
                  <Typography sx={{ mt: 0.35, color: "#1d2939", fontSize: 13, lineHeight: 1.5 }}>
                    {item.improve_plan || "등록된 개선대책이 없습니다."}
                  </Typography>
                  <Typography sx={{ mt: 0.7, color: "#667085", fontSize: 11.5 }}>
                    담당 {item.emp_name || "미지정"} · 예정일 {dateLabel(item.expected_date)}
                  </Typography>
                </Box>
              )}
              <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.15 }}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => onOpenSafety?.(item)}
                  sx={{ border: 0, p: 0, bgcolor: "transparent", color: "#4f46e5", font: "inherit", fontSize: 12, fontWeight: 700, cursor: "pointer", "&:hover": { color: "#3730a3", textDecoration: "underline" } }}
                >
                  안전관리 시스템에서 보기
                </Box>
              </Stack>
            </Box>
          ))}
          {safeItems.length === 0 && (
            <Box sx={{ py: 5, textAlign: "center", color: "#667085", fontSize: 13 }}>
              표시할 안전정보가 없습니다.
            </Box>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
